/**
 *    This component is a replacement for the native <a> / <Link> components.
 *
 *    In IDM and the Controller we don't use React Router everywhere, this component detects whether the router is set and
 *    if it is uses the react-router-link component (<Link>) else uses the native browser anchor tag (<a>).
 *
 *    It is also possible to overwrite that behaviour if there is a need to use links e.g. for downloads or switching between app contexts
 *    like going from the activation flow / login screen to the main app.
 */
import React from 'react';
import {Link, useLocation} from 'react-router-dom';

interface LinkInterface extends React.HTMLProps<HTMLAnchorElement> {
    forceRedirect?: boolean;
    download?: boolean;
    href: string;
    ref?: React.Ref<HTMLAnchorElement>;
}

const A = ({
    forceRedirect = false,
    download = false,
    children,
    href,
    ref,
    ...linkProps
}: LinkInterface): JSX.Element => {
    // Test to see if React Router is active.
    let routerSet = true;
    try {
        useLocation();
    } catch {
        routerSet = false;
    }

    if (routerSet) {
        return (
            // eslint-disable-next-line react/forbid-elements
            <Link
                to={href}
                ref={ref}
                reloadDocument={forceRedirect || download}
                // We never set the `download` attribute as it causes
                // problems when a user's session has expired.
                // Instead, we force a redirect to the actual URL (see above).
                download={false}
                data-download={download}
                {...linkProps}
            >
                {children}
            </Link>
        );
    }

    return (
        // eslint-disable-next-line react/forbid-elements
        <a
            href={href}
            ref={ref}
            // We never set the `download` attribute as it causes
            // problems when a user's session has expired.
            download={false}
            data-download={download}
            {...linkProps}
        >
            {children}
        </a>
    );
};

export default A;
